import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { fetchAdminMe } from '@/api/admin';
import { useAuth } from '@/contexts/AuthContext';

const AdminCtx = createContext(null);

/**
 * AdminProvider: probes /api/admin/me whenever the user's auth state changes.
 * Sets:
 *   admin: { id, username, email, is_admin } if 200
 *   denied: true if 403/401 or no JWT
 *   loading until the probe resolves
 */
export const AdminProvider = ({ children }) => {
  const { authUser, bootLoading } = useAuth();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  // Read-only ref so the refresh callback can check "do we already have an
  // admin loaded?" without including ``admin`` in its useCallback deps
  // (which would re-create the callback on every successful probe and
  // re-trigger the AdminProvider's useEffect, causing an infinite probe
  // loop). The ref mirrors ``admin`` for read-side branching only.
  const adminRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!authUser) {
      setAdmin(null);
      adminRef.current = null;
      setDenied(true);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await fetchAdminMe();
      setAdmin(data);
      adminRef.current = data;
      setDenied(false);
    } catch (e) {
      // Distinguish a REAL access denial (server explicitly returned 401/403)
      // from a transient network blip (ERR_CONNECTION_CLOSED, timeout, 5xx,
      // CORS preflight stutter). Only the former should flip the SPA into
      // the "Admin role required" gate \u2014 a network hiccup must not lock
      // an actual admin out of their own console mid-session.
      const status = e?.response?.status;
      const isExplicitDenial = status === 401 || status === 403;
      if (isExplicitDenial) {
        setAdmin(null);
        adminRef.current = null;
        setDenied(true);
      } else if (!adminRef.current) {
        // Network error on the FIRST probe (no prior state) \u2014 surface
        // the gate so the user knows something's off and can hit refresh.
        setAdmin(null);
        setDenied(true);
      }
      // Else: we already have a valid admin from a prior successful probe.
      // Keep it. The next user-triggered refresh() (or the next auth
      // state change) will retry the probe \u2014 we do NOT log the user out
      // for a transient network error.
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  useEffect(() => {
    if (bootLoading) return;
    refresh();
  }, [bootLoading, refresh]);

  return <AdminCtx.Provider value={{ admin, loading: loading || bootLoading, denied, refresh }}>{children}</AdminCtx.Provider>;
};

export const useAdmin = () => {
  const ctx = useContext(AdminCtx);
  if (!ctx) throw new Error('useAdmin must be used inside <AdminProvider>');
  return ctx;
};
